import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AnnualLeaseService } from '@app/landowner-admin-shared/services/annual-lease.service';
import RowClickArgs from '@app/models-internal/rowclick-args';
import AnnualLeaseSearchRequest from '@app/models/annual-lease-search-request';
import AnnualLeaseViewModel from '@app/models/annual-lease-view-model';
import PagedResults from '@app/models/paged-results';
import { ErrorHelperService, LogService, NotificationService } from '@app/services';
import { Subject, Subscription } from 'rxjs';
import { ReplacePipe } from '../../../common-components/pipes/replace.pipe';
import { SpinnerComponent } from '../../../common-components/spinner/spinner.component';
import { LinkToBookingDetailComponent } from '../../../common-components/link-to-booking-detail/link-to-booking-detail.component';
import { LinkToNewAdminListingComponent } from '../link-to-new-admin-listing/link-to-new-admin-listing.component';
import { LinkToNewListingDetailComponent } from '../../../common-components/link-to-new-listing-detail/link-to-new-listing-detail.component';
import { NgIf, NgFor, NgClass, PercentPipe, CurrencyPipe, DatePipe } from '@angular/common';

/**
 * Annual Lease Grid (shown in Admin and Landowner Listing Admin componennts)
 * showTitle - Default True
 * <annual-lease-grid #dataGrid [listingId]="listingId" [showTitle]="false" (rowClick)="onAnnualLeaseRowClick($event)"> </annual-lease-grid>
 */
@Component({
    selector: "annual-lease-grid",
    templateUrl: "./annual-lease-grid.component.html",
    styleUrls: ["./annual-lease-grid.component.less"],
    imports: [NgIf, NgFor, LinkToNewListingDetailComponent, LinkToNewAdminListingComponent, LinkToBookingDetailComponent, NgClass, SpinnerComponent, PercentPipe, CurrencyPipe, DatePipe, ReplacePipe]
})
export class AnnualLeaseGridComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'AnnualLeaseGridComponent';

  private showDevComments$: Subscription;

  /**
   * Subject to control observable completion
   */
  private unSubSub$ = new Subject<void>();

  public showDevComments: boolean;

  @Input()
  public hiddenColumns = ['escalator', 'membershipAmount'];

  @Input()
  public listingId: number;

  public retrieving = false;

  public deleting = false;

  @Input()
  public showTitle = true;

  /**
   * The paged data result (for future use if/when pagination is implemented)
   */
  private pageResult: PagedResults;

  @Output()
  public rowClick = new EventEmitter<RowClickArgs<AnnualLeaseViewModel>>();

  /**
   * The rows that are actually displayed in the grid
   */
  public rows: AnnualLeaseViewModel[];

  constructor(
    private log: LogService,
    private notify: NotificationService,
    private errorHelper: ErrorHelperService,
    private annualLeaseService: AnnualLeaseService
  ) {
    this.showDevComments$ = log.showDevComments.subscribe((x) => (this.showDevComments = x));
  }

  ngOnInit() {
    this.retrieve();
  }

  ngOnDestroy(): void {
    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();

    if (this.showDevComments$) {
      this.showDevComments$.unsubscribe();
    }
  }

  public onRefreshClick() {
    this.retrieve();
  }

  public isVisible(name: string) {
    return !this.hiddenColumns.some((x) => x === name);
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Retrieve annual leases from the server
   */
  public retrieve() {
    this.retrieving = true;

    var request = {} as AnnualLeaseSearchRequest;

    this.search(request);
  }

  public onDeleteClick(row: AnnualLeaseViewModel) {
    console.log(`${this._componentName}.onDeleteClick`);

    this.rowClick.emit({
      action: 'delete',
      componentName: this._componentName,
      data: row,
    });
  }

  public onEditClick(row: AnnualLeaseViewModel) {
    console.log(`${this._componentName}.onEditClick`);

    this.rowClick.emit({
      action: 'edit',
      componentName: this._componentName,
      data: row,
    });
  }

  public onRowClick(row: AnnualLeaseViewModel) {
    console.log(`${this._componentName}.onRowClick row:`, row);
  }

  public search(request: AnnualLeaseSearchRequest) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.search request=`, request);
    }

    this.retrieving = true;

    if (this.listingId) {
      request.listingId = this.listingId;
    }

    if (!request.pageNumber) {
      request.pageNumber = 1;
    }

    this.annualLeaseService.searchAnnualLeases(request).subscribe(
      (result) => {
        console.log(`${this._componentName} subscription result=`, result);

        this.pageResult = result;

        this.rows = result.results.map((x) => x as AnnualLeaseViewModel);

        this.retrieving = false;
      },
      (err) => {
        console.error(err);

        const msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

        console.error(msg);

        
        this.notify.error(msg, null);

        this.retrieving = false;
      }
    );
  }
}
